"use client";
import { useEffect, useMemo, useState } from "react";
import Image from "next/image";
import dynamic from "next/dynamic";
import { motion } from "framer-motion";
import { formatIndianNumber } from "@/utils/commonFunctions";
import { calculateSIPValue } from "@/utils/allCalculators";
import { useLoginModal } from "@/utils/useLoginModal";
import { ROOT_URL } from "@/utils/gloableVariables";
import { SuitabilityBestReturn } from "@/type/commonTypes";

// Only needed after "Schedule a Call" is clicked (brings its own Lottie + calendar)
const ScheduleCallModal = dynamic(() => import("../tabs/ScheduleCall"), { ssr: false });

const SuitablePointIcon = () => (
  <>
    <path
      d="M9.99996 18.3307C14.5833 18.3307 18.3333 14.5807 18.3333 9.9974C18.3333 5.41406 14.5833 1.66406 9.99996 1.66406C5.41663 1.66406 1.66663 5.41406 1.66663 9.9974C1.66663 14.5807 5.41663 18.3307 9.99996 18.3307Z"
      fill="#1BB300"
    />
    <path
      d="M6.45837 9.99896L8.81671 12.3573L13.5417 7.64062"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </>
);

const NotSuitablePointIcon = () => (
  <>
    <path
      d="M10.0001 18.3307C14.5834 18.3307 18.3334 14.5807 18.3334 9.9974C18.3334 5.41406 14.5834 1.66406 10.0001 1.66406C5.41675 1.66406 1.66675 5.41406 1.66675 9.9974C1.66675 14.5807 5.41675 18.3307 10.0001 18.3307Z"
      fill="#EE7D55"
    />
    <path d="M10 6.66406V10.8307" stroke="white" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
    <path
      d="M9.99561 13.3359H10.0031"
      stroke="white"
      strokeWidth="1.66667"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </>
);

const ProjectionIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="w-5 h-5 xs:w-6 xs:h-6 "
  >
    <path d="M6.88 18.1481V16.0781" stroke="#1A51AA" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M12 18.1478V14.0078" stroke="#1A51AA" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M17.12 18.1497V11.9297" stroke="#1A51AA" strokeWidth="1.5" strokeLinecap="round" />
    <path
      d="M17.12 5.85156L16.66 6.39156C14.11 9.37156 10.69 11.4816 6.88 12.4316"
      stroke="#1A51AA"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <path
      d="M14.1899 5.85156H17.1199V8.77156"
      stroke="#1A51AA"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CartIcon = () => (
  <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M1.54395 3.16406C1.54395 3.03145 1.59662 2.90428 1.69039 2.81051C1.78416 2.71674 1.91134 2.66406 2.04395 2.66406H2.83128C3.19294 2.66408 3.54242 2.79477 3.81535 3.03206C4.08829 3.26934 4.2663 3.59725 4.31661 3.9554L4.35728 4.2454H13.6673C13.9162 4.2454 14.1612 4.30736 14.3803 4.42569C14.5993 4.54402 14.7854 4.715 14.9219 4.92321C15.0583 5.13143 15.1408 5.37032 15.1619 5.61837C15.183 5.86642 15.142 6.11581 15.0426 6.34406L13.2639 10.4294C13.1474 10.6972 12.9551 10.9252 12.7108 11.0853C12.4665 11.2454 12.1807 11.3307 11.8886 11.3307H5.64795C5.28628 11.3307 4.93681 11.2 4.66387 10.9627C4.39094 10.7254 4.21292 10.3975 4.16261 10.0394L3.32661 4.09473C3.30989 3.97527 3.25054 3.86588 3.15951 3.78674C3.06848 3.70759 2.9519 3.66402 2.83128 3.66406H2.04461C1.912 3.66406 1.78483 3.61138 1.69106 3.51762C1.59729 3.42385 1.54461 3.29667 1.54461 3.16406M4.49861 5.2454L5.15328 9.90006C5.17001 10.0195 5.22935 10.1289 5.32038 10.2081C5.41141 10.2872 5.52799 10.3308 5.64861 10.3307H11.8893C11.9866 10.3308 12.0819 10.3025 12.1634 10.2492C12.2449 10.1959 12.309 10.12 12.3479 10.0307L14.1259 5.94406C14.1589 5.86805 14.1724 5.78506 14.1653 5.70254C14.1582 5.62001 14.1307 5.54055 14.0853 5.47128C14.0399 5.402 13.978 5.3451 13.9051 5.30567C13.8323 5.26625 13.7508 5.24553 13.6679 5.2454H4.49861Z"
      fill="white"
    />
    <path
      d="M4.52271 13.5026C4.52271 13.1932 4.64562 12.8964 4.86441 12.6776C5.08321 12.4589 5.37995 12.3359 5.68937 12.3359C5.99879 12.3359 6.29554 12.4589 6.51433 12.6776C6.73312 12.8964 6.85604 13.1932 6.85604 13.5026C6.85604 13.812 6.73312 14.1088 6.51433 14.3276C6.29554 14.5464 5.99879 14.6693 5.68937 14.6693C5.37995 14.6693 5.08321 14.5464 4.86441 14.3276C4.64562 14.1088 4.52271 13.812 4.52271 13.5026ZM11.38 12.3359C11.0706 12.3359 10.7739 12.4589 10.5551 12.6776C10.3363 12.8964 10.2134 13.1932 10.2134 13.5026C10.2134 13.812 10.3363 14.1088 10.5551 14.3276C10.7739 14.5464 11.0706 14.6693 11.38 14.6693C11.6895 14.6693 11.9862 14.5464 12.205 14.3276C12.4238 14.1088 12.5467 13.812 12.5467 13.5026C12.5467 13.1932 12.4238 12.8964 12.205 12.6776C11.9862 12.4589 11.6895 12.3359 11.38 12.3359Z"
      fill="white"
    />
  </svg>
);

export default function SuitabilityResultCard({
  isMatch,
  handleCartButton,
  name,
  imgCode,
  points,
  bestReturn,
  closeModal,
}: {
  isMatch: boolean;
  closeModal: () => void;
  handleCartButton: () => void;
  name: string;
  imgCode: string;
  points: string[];
  bestReturn: SuitabilityBestReturn;
}) {
  const [schedule, setSchedule] = useState(false);
  const { isLoggedIn, open, setModalKey } = useLoginModal();

  const handleLogin = () => {
    if (isLoggedIn) {
      if (isMatch) {
        handleCartButton();
      } else {
        setSchedule(true);
      }
    } else {
      open();
      setModalKey("suitability-test");
    }
  };

  const calculatedValue = useMemo(
    () => calculateSIPValue(5000, Number(bestReturn.year), Number(bestReturn.value)),
    [bestReturn.year, bestReturn.value],
  );

  // Warm up the Schedule Call chunk as soon as it can be needed
  useEffect(() => {
    if (!isMatch) import("../tabs/ScheduleCall");
  }, [isMatch]);

  if (schedule) return <ScheduleCallModal closeModal={closeModal} />;

  return (
    <motion.div
      className="h-full xs:p-5 relative"
      initial={{ scale: 0.95, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
    >
      <div className="flex items-center justify-between gap-6 mb-5 relative">
        <div
          className={`flex-1 flex items-center gap-2 rounded-xl px-3 py-2.5 ${isMatch ? "bg-[#DFFFEA]" : "bg-[#FFE7DF]"}`}
        >
          <Image
            src={`${ROOT_URL}img/AMCsLogo/${imgCode}.png`}
            alt="fund image"
            width={30}
            height={30}
            className="w-6 h-6 xs:w-7.5 xs:h-7.5 rounded-full object-contain shrink-0"
          />
          <p className="text-primaryBlack font-semibold text-sm xs:text-base line-clamp-1" title={name}>
            {name}
          </p>
        </div>
        <Image
          src={`/next_assets/images/scheme-details/${isMatch ? "pros-img" : "not-suitable-fund"}.svg`}
          alt={`${isMatch ? "suitable fund image" : "not suitable fund image"}`}
          width={50}
          height={50}
          className="shrink-0 w-9 h-9 xs:w-half-px xs:h-half-px absolute right-3 bottom-0 translate-y-1/2 xs:static"
        />
      </div>

      <ul className={`space-y-4 px-5 xs:px-0 ${isMatch ? "" : "mt-14 xs:mt-0"}`}>
        {points.map((point, index) => (
          <li key={index} className="flex items-center gap-2 text-2xs text-[#2C2C2C] leading-4" title={point}>
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="shrink-0"
            >
              {isMatch ? <SuitablePointIcon /> : <NotSuitablePointIcon />}
            </svg>

            <span className="line-clamp-1">{point}</span>
          </li>
        ))}
      </ul>

      {isMatch ? (
        <div className="flex flex-col xs:flex-row items-center justify-between gap-5 mt-10 xs:mt-6 mx-5 xs:mx-0">
          <div className="bg-[#F0F2FF] rounded-xl p-4 xs:p-5 text-center">
            <p className="flex gap-1.5 justify-center items-center mb-5 text-sm xs:text-base text-primaryBlue line-clamp-2">
              <ProjectionIcon />
              {bestReturn.year} Year Projection
            </p>

            <p className="text-[#2C2C2C] mb-3 text-xs xs:text-2xs line-clamp-2">
              If you Invest <strong>₹5,000/month</strong> Your portfolio could be:
            </p>

            <p className="text-[#1BB300] text-xl xs:text-2xl font-bold mb-1">{formatIndianNumber(calculatedValue)}</p>

            <p className="font-medium text-[#2C2C2C] text-xs">(Based on estimated returns)</p>
          </div>
          <div className="shrink-0">
            <div className="text-sm mb-5 text-center xs:text-left">
              {bestReturn.year}Y Returns
              <p className="text-xl">{bestReturn.value}% p.a.</p>
            </div>
            <button
              onClick={handleLogin}
              className="flex items-center justify-center gap-1 w-full ml-auto border border-[#1A51AA80] rounded-lg text-xs italic text-center font-medium text-white py-2 px-7 bg-[radial-gradient(50%_50%_at_50%_50%,#1A51AA_0%,#083E95_100%)] hover:bg-[radial-gradient(50%_50%_at_50%_50%,#778db0_0%,#083E95_100%)]"
            >
              Add to Cart
              <CartIcon />
            </button>
          </div>
        </div>
      ) : (
        <div className="relative mt-24 xs:mt-6 flex items-center xs:gap-5 bg-[radial-gradient(100%_463.91%_at_0%_8.88%,#002545_0%,#ADADAD_78.08%,#000000_100%)] overflow-hidden px-4 xs:px-6 xs:rounded-4xl">
          <div>
            <p className="text-white font-semibold text-sm xs:text-base leading-5 xs:leading-6 mb-4 xs:mb-5 line-clamp-2">
              Confused about where to invest? In 15 minutes, our experts will make it clear.
            </p>
            <div className="bg-[linear-gradient(90deg,rgba(255,255,255,0.175)_0%,rgba(255,255,255,0.5)_49.66%,rgba(255,255,255,0.175)_100%)] max-w-fit p-0.5 rounded-lg">
              <button
                onClick={handleLogin}
                className="bg-black rounded-lg text-white font-semibold text-xs xs:text-sm xs:leading-4.5 py-2 px-4"
              >
                Schedule a Call
              </button>
            </div>
          </div>
          <Image
            src="/next_assets/images/suitability-modal/not-suitable-strip.png"
            alt="Strip Image"
            width={160}
            height={170}
            className="w-31.5 xs:w-40 h-34.75 xs:h-42.5 relative xs:static -right-3 bottom-0 object-contain shrink-0"
          />
        </div>
      )}
    </motion.div>
  );
}
